import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../css/Login.css';

export default function Login({ initialMode = "login" }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [signupStep, setSignupStep] = useState(1);
  
  // Enhanced signup fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  
  const { login, signup, resetPassword, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  // Update mode when initialMode prop changes
  useEffect(() => {
    setIsLogin(initialMode === "login");
    setError('');
    setConfirmPassword('');
    setSignupStep(1);
    setProgress(0);
    setAchievements([]);
  }, [initialMode]);

  // Gamified progress tracking
  const [progress, setProgress] = useState(0);
  const [achievements, setAchievements] = useState([]);

  useEffect(() => {
    if (isLogin) {
      setProgress(0);
      setSignupStep(1);
    } else {
      setProgress(signupStep === 1 ? 50 : 100);
    }
  }, [signupStep, isLogin]);

  // Achievement system
  useEffect(() => {
    if (firstName && lastName) {
      if (!achievements.includes('name_complete')) {
        setAchievements([...achievements, 'name_complete']);
      }
    }
    if (email && password && password === confirmPassword) {
      if (!achievements.includes('credentials_complete')) {
        setAchievements([...achievements, 'credentials_complete']);
      }
    }
  }, [firstName, lastName, email, password, confirmPassword, achievements]);

  // eslint-disable-next-line no-unused-vars
  const showAchievement = (message) => {
    const achievement = document.createElement('div');
    achievement.className = 'achievement-popup';
    achievement.textContent = message;
    document.body.appendChild(achievement);
    
    setTimeout(() => {
      achievement.classList.add('show');
    }, 100);
    
    setTimeout(() => {
      achievement.classList.remove('show');
      setTimeout(() => document.body.removeChild(achievement), 300);
    }, 3000);
  };

  const nextStep = () => {
    if (signupStep === 1) {
      // Basic validation for step 1 (still required for account creation)
      if (!firstName || !lastName || !phoneNumber || !email || !password || !confirmPassword) {
        setError('Please fill in all basic information fields');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      setSignupStep(2);
      setError('');
      setProgress(100);
    }
  };

  const prevStep = () => {
    if (signupStep === 2) {
      setSignupStep(1);
      setProgress(50);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (!isLogin) {
      // Validation required for step 2 - company, industry, and country are now required
      if (!companyName || !industry || !country) {
        setError('Please fill in all required fields: Company Name, Industry, and Country');
        return;
      }
    }

    try {
      setError('');
      setLoading(true);
      
      if (isLogin) {
        await login(email, password);
        navigate('/home');
      } else {
        // Enhanced signup with all user data
        const userData = {
          firstName,
          lastName,
          phoneNumber,
          companyName,
          country,
          industry
        };
        await signup(email, password, userData);
        
        // Show verification message instead of navigating
        setShowVerificationMessage(true);
        setVerificationEmail(email);
        setError(''); // Clear any previous errors
        
        // Reset form
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setCompanyName('');
        setCountry('');
        setIndustry('');
        setSignupStep(1);
        setProgress(0);
        setAchievements([]);
      }
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  }

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email first');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setError('Password reset email sent! Check your inbox.');
    } catch (error) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail(verificationEmail);
      setError('');
      // Show success message
      if (typeof window !== 'undefined') {
        const successMessage = document.createElement('div');
        successMessage.className = 'cyber-success';
        successMessage.textContent = '✅ Verification email resent successfully!';
        successMessage.style.position = 'fixed';
        successMessage.style.top = '20px';
        successMessage.style.right = '20px';
        successMessage.style.zIndex = '9999';
        document.body.appendChild(successMessage);
        
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 5000);
      }
    } catch (error) {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    if (isLogin) {
      // Navigate to signup page
      navigate('/signup');
    } else {
      // Navigate to login page
      navigate('/login');
    }
  };

  return (
    <div className="login-container">
      {/* Animated Background */}
      <div className="cyber-grid"></div>
      <div className="floating-particles"></div>
      <div className="energy-field"></div>
      
      {/* Progress Bar */}
      {!isLogin && (
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text">Step {signupStep} of 2 - {progress}% Complete</div>
        </div>
      )}

      {/* Achievement Display */}
      {!isLogin && achievements.length > 0 && (
        <div className="achievements-display">
          {achievements.map((achievement, index) => (
            <div key={index} className="achievement-badge">
              {achievement === 'name_complete' ? '🎯' : '🔐'}
            </div>
          ))}
        </div>
      )}

      <div className="login-card">
        <div className="card-header">
          <h2 className="glitch-text" data-text={isLogin ? 'Login to Exodia' : 'Join Exodia'}>
            {isLogin ? 'Login to Exodia' : 'Join Exodia'}
          </h2>
        </div>
        
        {error && <div className="cyber-error">{error}</div>}
        
        <form onSubmit={handleSubmit} className="cyber-form">
          {isLogin ? (
            // Login Form
            <>
              <div className="form-group">
                <label className="cyber-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  className="cyber-field"
                />
              </div>
              
              <div className="form-group">
                <label className="cyber-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="cyber-field"
                />
              </div>
            </>
          ) : (
            // Multi-step Signup Form
            <>
              {signupStep === 1 ? (
                // Step 1: Basic Information
                <div className="signup-step">
                  <div className="step-indicator">
                    <div className="step active">1</div>
                    <div className="step">2</div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="cyber-label">First Name *</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        placeholder="First name (required)"
                        className="cyber-field"
                      />
                    </div>
                    <div className="form-group">
                      <label className="cyber-label">Last Name *</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        placeholder="Last name (required)"
                        className="cyber-field"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="cyber-label">Phone Number *</label>
                    <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                        placeholder="Enter your phone number (required)"
                        className="cyber-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="cyber-label">Email *</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="Enter your email (required)"
                        className="cyber-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="cyber-label">Password *</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="Create a password (required)"
                        className="cyber-field"
                    />
                    <p className="password-info cyber-text">
                      Create a password for your account. We'll also send a verification email.
                    </p>
                  </div>
                  
                  <div className="form-group">
                    <label className="cyber-label">Confirm Password *</label>
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        placeholder="Confirm your password (required)"
                        className="cyber-field"
                    />
                    {confirmPassword && (
                      <p className={password === confirmPassword ? 'password-success cyber-text' : 'password-error cyber-text'}>
                        {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </p>
                    )}
                  </div>
                  
                  <button 
                    type="button" 
                    onClick={nextStep}
                    className="next-btn cyber-btn"
                  >
                    Next Step → Professional Info
                  </button>
                </div>
              ) : (
                // Step 2: Professional Information
                <div className="signup-step">
                  <div className="step-indicator">
                    <div className="step completed">1</div>
                    <div className="step active">2</div>
                  </div>
                  
                  <div className="form-group">
                    <label className="cyber-label">Company Name *</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter your company name (required)"
                      className="cyber-field"
                      required
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="cyber-label">Country *</label>
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="cyber-field"
                        required
                      >
                        <option value="">Select country (required)</option>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="UK">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="JP">Japan</option>
                        <option value="IN">India</option>
                        <option value="BR">Brazil</option>
                        <option value="MX">Mexico</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="cyber-label">Industry *</label>
                      <select
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        className="cyber-field"
                        required
                      >
                        <option value="">Select industry (required)</option>
                        <option value="Technology">Technology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Finance">Finance</option>
                        <option value="Education">Education</option>
                        <option value="Gaming">Gaming</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Retail">Retail</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="step-navigation">
                    <button 
                      type="button" 
                      onClick={prevStep}
                      className="prev-btn cyber-btn secondary"
                    >
                      ← Back to Basic Info
                    </button>
                    
                    <button 
                      type="submit" 
                      disabled={loading}
                      className="submit-btn cyber-btn primary"
                    >
                      {loading ? 'Creating Account...' : 'Complete Signup & Join Exodia!'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          
          {isLogin && (
            <button type="submit" disabled={loading} className="submit-btn cyber-btn primary">
              {loading ? 'Logging In...' : 'Login to Exodia'}
            </button>
          )}
        </form>
        
        <div className="auth-actions">
          <button 
            type="button" 
            onClick={handleToggleMode}
            className="toggle-btn cyber-link"
          >
            {isLogin ? 'Need an account? Create Account' : 'Already have an account? Sign In'}
          </button>
          
          {isLogin && (
            <button 
              type="button" 
              onClick={handlePasswordReset}
              className="reset-btn cyber-link"
            >
              Forgot Password?
            </button>
          )}
        </div>
      </div>

      {/* Email Verification Message */}
      {showVerificationMessage && (
        <div className="verification-overlay">
          <div className="verification-card">
            <div className="verification-header">
              <h3>📧 Check Your Email!</h3>
              <p>We've sent a verification link to <strong>{verificationEmail}</strong></p>
            </div>
            
            <div className="verification-steps">
              <div className="verification-step">
                <span className="step-number">1</span>
                <span className="step-text">Check your email inbox (and spam folder)</span>
              </div>
              <div className="verification-step">
                <span className="step-number">2</span>
                <span className="step-text">Click the verification link in the email</span>
              </div>
              <div className="verification-step">
                <span className="step-number">3</span>
                <span className="step-text">Return here and sign in with your account</span>
              </div>
            </div>
            
            <div className="verification-actions">
              <button 
                type="button" 
                onClick={handleResendVerification}
                className="cyber-btn secondary"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
              
              <button 
                type="button" 
                onClick={() => {
                  setShowVerificationMessage(false);
                  setIsLogin(true);
                }}
                className="cyber-btn primary"
              >
                Go to Sign In
              </button>
            </div>
            
            <div className="verification-note">
              <p>💡 <strong>Tip:</strong> After verifying your email, you'll be able to sign in and access your Exodia dashboard!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
